import { ChevronLeft, X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useSession } from '../api/queries';
import { OfflineBanner } from '../components/ui/Banner';
import { Spinner } from '../components/ui/Spinner';
import { useDialogBehaviour } from '../components/ui/useDialogBehaviour';
import { cx } from '../lib/cx';
import { useT } from '../lib/i18n';
import { commonMessages } from '../lib/i18n/messages/common';
import { useInFlowDialog, FlowDialogContext } from './flow-context';
import { setReturnTo, useKeyboardInset, useToastOffset } from './hooks';
import { Sidebar } from './Sidebar';
import { TabBar } from './TabBar';

export function SkipLink() {
  const t = useT(commonMessages);
  return (
    <a
      href="#main"
      className="sr-only z-[70] rounded-control bg-primary px-4 py-3 font-semibold text-primary-fg focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
    >
      {t('skipToContent')}
    </a>
  );
}

/** Full-screen spinner, only for the very first boot (DESIGN 3.4). */
export function BootSplash() {
  const t = useT(commonMessages);
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas" role="status">
      <span className="sr-only">{t('loading')}</span>
      <div className="flex flex-col items-center gap-4">
        <span className="font-display text-3xl font-bold tracking-tight">split</span>
        <Spinner className="h-6 w-6 text-ink-muted" />
      </div>
    </div>
  );
}

/** Signed-in area: redirects to /signin (remembering where to return) when signed out. */
export function RequireAuth() {
  const { data, isPending, isError } = useSession();
  const location = useLocation();
  if (isPending) return <BootSplash />;
  if (data === null || (isError && data === undefined)) {
    setReturnTo(location.pathname + location.search);
    return <Navigate to="/signin" replace />;
  }
  return <Outlet />;
}

/** Moves focus to the screen's h1 after navigation (DESIGN 7 Focus). */
export function RouteFocus() {
  const location = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const id = requestAnimationFrame(() => {
      const h1 = document.querySelector<HTMLElement>('[role="dialog"] h1, main h1');
      if (h1) {
        h1.tabIndex = -1;
        h1.focus({ preventScroll: true });
      }
      window.scrollTo({ top: 0 });
    });
    return () => cancelAnimationFrame(id);
  }, [location.pathname]);
  return null;
}

/** Tab roots, group and person detail: sidebar on desktop, tab bar on mobile. */
export function TabLayout() {
  return (
    <div className="flex min-h-dvh bg-canvas lg:bg-sunken">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col lg:bg-canvas">
        <OfflineBanner />
        <main id="main" className="mx-auto flex w-full max-w-[560px] flex-1 flex-col lg:max-w-2xl">
          <Outlet />
        </main>
        <TabBar />
      </div>
    </div>
  );
}

/** Flows opened by URL (not as a dialog): no tab bar; sidebar on desktop. */
export function FlowLayout() {
  return (
    <div className="flex min-h-dvh bg-canvas lg:bg-sunken">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col lg:bg-canvas">
        <OfflineBanner />
        <main
          id="main"
          className="mx-auto flex w-full max-w-[560px] flex-1 flex-col lg:max-w-md lg:py-8 lg:has-[.flow-wide]:max-w-4xl"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** Public screens (setup, invite, sign in): no navigation chrome. */
export function PublicLayout() {
  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <OfflineBanner />
      <main id="main" className="mx-auto flex w-full max-w-[480px] flex-1 flex-col">
        <Outlet />
      </main>
    </div>
  );
}

/** Desktop dialog wrapper for flows opened over a background page. */
export function FlowDialog({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const panel = useRef<HTMLDivElement>(null);
  const t = useT(commonMessages);
  useDialogBehaviour(panel, onClose, 'container');
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-6">
      <button
        type="button"
        tabIndex={-1}
        aria-label={t('close')}
        className="absolute inset-0 animate-fade-in cursor-default bg-ink/40"
        onClick={onClose}
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className="relative flex max-h-[90dvh] w-full max-w-md has-[.flow-wide]:max-w-4xl animate-fade-in flex-col overflow-hidden rounded-sheet bg-canvas shadow-pop focus:outline-none"
      >
        <FlowDialogContext value={true}>{children}</FlowDialogContext>
      </div>
    </div>
  );
}

/**
 * Header + scrolling body + sticky bottom action bar for flows (add expense, settle up,
 * settings…). Riding above the keyboard via `visualViewport` (DESIGN 4.10).
 */
export function FlowFrame({
  title,
  titleId,
  onClose,
  closeIcon = 'back',
  closeLabel,
  aside,
  footer,
  footerNote,
  children,
  className,
  bodyClassName,
}: {
  title: ReactNode;
  titleId?: string;
  onClose: () => void;
  closeIcon?: 'back' | 'close';
  closeLabel?: string;
  aside?: ReactNode;
  footer?: ReactNode;
  footerNote?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  const t = useT(commonMessages);
  const inDialog = useInFlowDialog();
  const keyboard = useKeyboardInset();
  useToastOffset(footer ? 96 : 16);
  const Icon = closeIcon === 'close' ? X : ChevronLeft;
  return (
    <div
      className={cx(
        'flex flex-col',
        inDialog ? 'max-h-[90dvh] min-h-0' : 'min-h-dvh lg:min-h-0',
        className,
      )}
    >
      <header className="flex shrink-0 items-center justify-between gap-2 px-3 pt-[max(0.75rem,var(--safe-top))]">
        <button
          type="button"
          className="iconbtn"
          aria-label={closeLabel ?? (closeIcon === 'close' ? t('close') : t('back'))}
          onClick={onClose}
        >
          <Icon className="ic" strokeWidth={1.75} aria-hidden />
        </button>
        <h1 id={titleId} className="min-w-0 truncate font-display text-lg font-bold">
          {title}
        </h1>
        <div className="flex min-w-11 justify-end">{aside}</div>
      </header>
      <div
        className={cx(
          'flex-1 px-5 pt-4 pb-6',
          inDialog && 'min-h-0 overflow-y-auto',
          bodyClassName,
        )}
      >
        {children}
      </div>
      {footer !== undefined && (
        <div
          className="sticky bottom-0 z-10 shrink-0 border-t border-line bg-canvas/95 px-5 pt-3 pb-[calc(1.25rem+var(--safe-bottom))] backdrop-blur"
          style={keyboard ? { bottom: keyboard, paddingBottom: 12 } : undefined}
        >
          {footerNote}
          {footer}
        </div>
      )}
    </div>
  );
}
